/**
 * @file BodycamContext.tsx
 *
 * @description This context stores critical information such as session data, officer profiles, UI state, and model information
 * that needs to be accessible throughout the component tree.
 *
 * @module Context
 * @requires React
 */
import React, { createContext, useEffect, useState } from "react";
import { OfficerProfile } from "../shared/interfaces/OfficerProfile";
import { NarrativeTemplate } from "../shared/interfaces/ReportGenReport";
import { FieldFormatMap } from "../shared/interfaces/DynamicModel";
import { Officer } from "../shared/interfaces/Officer";
import { getOfficers } from "../api/officersApi";
import { usePermissions } from "../hooks/usePermissions";
import { canReadUsers } from "../shared/auth/authorization";
import { useAuthContext } from "./AuthContext";

/**
 * Interface defining the structure of the BodycamContext.
 * @interface BodycamContextProps
 * @property {[string, React.Dispatch<React.SetStateAction<string>>]} sessionInfo - Session ID and its setter function.
 * @property {[OfficerProfile | null, React.Dispatch<React.SetStateAction<OfficerProfile | null>>]} userInfo -
 *   Authenticated officer profile information and its setter function.
 * @property {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} openSidebar -
 *   State to control sidebar visibility and its setter function.
 * @property {[FieldFormatMap | null, React.Dispatch<React.SetStateAction<FieldFormatMap | null>>]} modelInformation -
 *   Information about the bodycam model or other hardware specifications and its setter function.
 */
interface BodycamContextProps {
  sessionInfo: [string, React.Dispatch<React.SetStateAction<string>>];
  userInfo: [
    OfficerProfile | null,
    React.Dispatch<React.SetStateAction<OfficerProfile | null>>
  ];
  openSidebar: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  modelInformation: [
    FieldFormatMap | null,
    React.Dispatch<React.SetStateAction<FieldFormatMap | null>>
  ];
  customReports: [
    NarrativeTemplate[] | null,
    React.Dispatch<React.SetStateAction<NarrativeTemplate[] | null>>
  ];
  stationOfficers: [
    Officer[],
    React.Dispatch<React.SetStateAction<Officer[]>>
  ];
}

/**
 * Props for the BodycamContextProvider component.
 * @interface ContextProviderProps
 * @property {React.ReactNode} children - Child components that will have access to the context.
 */
interface ContextProviderProps {
  children: React.ReactNode;
}

/**
 * Creates the BodycamContext with default values.
 * These defaults will be overridden by the actual state in the BodycamContextProvider.
 */
const BodycamContext = createContext<BodycamContextProps>({
  sessionInfo: [
    "",
    (() => {}) as React.Dispatch<React.SetStateAction<string>>,
  ],
  userInfo: [
    null,
    (() => {}) as React.Dispatch<React.SetStateAction<OfficerProfile | null>>,
  ],
  openSidebar: [
    false,
    (() => {}) as React.Dispatch<React.SetStateAction<boolean>>,
  ],
  modelInformation: [
    null,
    (() => {}) as React.Dispatch<React.SetStateAction<FieldFormatMap | null>>,
  ],
  customReports: [
    null,
    (() => {}) as React.Dispatch<React.SetStateAction<NarrativeTemplate[] | null>>,
  ],
  stationOfficers: [
    [],
    (() => {}) as React.Dispatch<React.SetStateAction<Officer[]>>,
  ],
});

/**
 * @name BodycamContextProvider
 *
 * @description Provider component that wraps the application to make the context available
 * to all child components.
 *
 * @param {ContextProviderProps} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped with the context
 *
 * @returns {JSX.Element} BodycamContext.Provider wrapping the children
 */
function BodycamContextProvider({ children }: ContextProviderProps) {
    const permissions = usePermissions();
  const { organizationId } = useAuthContext();

  // Initialize state hooks for different pieces of application state

  /**
   * State for storing the current session identifier.
   * Used for authentication and session tracking.
   */
  const sessionInfo = useState<string>("");

  /**
   * State for storing the authenticated officer's profile information.
   * Contains details like badge number, name, rank, etc.
   */
  const userInfo = useState<OfficerProfile | null>(null);

  /**
   * State for controlling the visibility of the application sidebar.
   * Used for responsive design and UI state management.
   */
  const openSidebar = useState<boolean>(false);

  // State for storing information about the bodycam model or other equipment details.
  const modelInformation = useState<FieldFormatMap | null>(null);

  /**
   * State for storing custom reports specific to the officer's station.
   * This can include various report types that the officer can generate.
   */
  const customReports = useState<NarrativeTemplate[] | null>(null);

  /**
   * State for storing officers within the station to drive filter pickers.
   */
  const [stationOfficers, setStationOfficers] = useState<Officer[]>(
      []
  );

  useEffect(() => {
    if (!organizationId || !canReadUsers(permissions)) return;
    getOfficers(organizationId)
      .then((users) => {
        const officers: Officer[] = users.users.map((user) => ({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          badgeNumber: user.metadata.badge_number,
          email: user.email,
          department: user.metadata.department,
          position: user.metadata.position,
          eforceUserId: null,
          stationId: organizationId,
        }));
        setStationOfficers(officers);
      })
      .catch((error) => {
        console.error("Failed to load officers", error);
      });
  }, [organizationId]);

  // Combine all state variables into a single context object
  const context: BodycamContextProps = {
    sessionInfo,
    userInfo,
    openSidebar,
    modelInformation,
    customReports,
    stationOfficers: [stationOfficers, setStationOfficers],
  };
  return (
    <BodycamContext.Provider value={context}>
      {children}
    </BodycamContext.Provider>
  );
}

export { BodycamContextProvider, BodycamContext };
